import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { request } from 'graphql-request';
import useGraphqlRequestParams from '@elementary/common-ui/hooks/useGraphqlRequestParams';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import {
  Filter,
  TestExecutionField,
} from '@elementary/common-ui/services/gql/types';
import { Totals } from '@elementary/common-ui/types';
import { TEST_RESULTS_GROUP_STATUS_SUMMARY } from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK } from '@elementary/common-ui/constants';
import { statusMap, emptyTotals } from 'shared/utils/testExecutions';
import {
  ApiTestResultsFilterOptionsResponse,
  getTestResultsFilterOptionsQuery,
} from './api';
import { FIELD_TO_COLUMN_MAP } from './consts';

type Args = {
  groupByColumnId: string | null;
  statusValues: string[];
  selectedAssetIds: string[];
  selectedTestIds: string[];
  selectedIdsHash: string;
  baseTestExecutionFilters: Filter[];
  search: string;
  envid: string | undefined;
  enabled: boolean;
};

const useGroupStatusSummaries = ({
  groupByColumnId,
  statusValues,
  selectedAssetIds,
  selectedTestIds,
  selectedIdsHash,
  baseTestExecutionFilters,
  search,
  envid,
  enabled,
}: Args): Record<string, Totals> => {
  const { accessToken, environmentId, gqlEndpoint } = useGraphqlRequestParams({
    envId: envid,
  });

  const queryEnabled =
    enabled &&
    !!groupByColumnId &&
    environmentId !== undefined &&
    !!accessToken;

  const results = useQueries({
    queries: queryEnabled
      ? statusValues.map((statusValue) => ({
          queryKey: [
            TEST_RESULTS_GROUP_STATUS_SUMMARY,
            envid,
            selectedIdsHash,
            DEFAULT_DAYS_BACK,
            baseTestExecutionFilters,
            search,
            groupByColumnId,
            statusValue,
          ],
          queryFn: () =>
            request<ApiTestResultsFilterOptionsResponse>(
              gqlEndpoint,
              getTestResultsFilterOptionsQuery,
              {
                testIds: selectedTestIds.length ? selectedTestIds : null,
                assetIds: selectedAssetIds.length ? selectedAssetIds : null,
                daysBack: DEFAULT_DAYS_BACK,
                search: search || null,
                testExecutionFilters: [
                  ...baseTestExecutionFilters,
                  {
                    property: 'STATUS',
                    operator: 'equals',
                    value: [statusValue],
                  },
                ],
              },
              { authorization: `Bearer ${accessToken}` },
            ).then((response) => ({ statusValue, response })),
          staleTime: Infinity,
        }))
      : [],
  });

  const signature = results
    .map((result) => `${result.dataUpdatedAt}`)
    .join('|');

  return useMemo(() => {
    if (!groupByColumnId) return {};
    const totalsByGroup: Record<string, Totals> = {};
    results.forEach((result) => {
      if (!result.data) return;
      const { statusValue, response } = result.data;
      const totalsKey = statusMap[statusValue];
      if (!totalsKey) return;
      const parsed = parseGraphqlFilterOptions<TestExecutionField>(
        response.latestTestExecutionsFilterOptions || [],
        FIELD_TO_COLUMN_MAP,
      );
      (parsed[groupByColumnId] ?? []).forEach((option) => {
        if (!totalsByGroup[option.value]) {
          totalsByGroup[option.value] = emptyTotals();
        }
        totalsByGroup[option.value][totalsKey] += option.count ?? 0;
      });
    });
    return totalsByGroup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, groupByColumnId]);
};

export default useGroupStatusSummaries;
